import React from "react";
import { Box, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { monoFontSx } from "../ThemeContext";

const NICE_STEPS = [1, 2, 2.5, 5, 10];

// Rounds up to a "clean" ceiling (nearest 1/2/2.5/5/10 x a power of ten) so
// the scale has readable tick marks instead of an arbitrary max.
function niceCeiling(value) {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const step = NICE_STEPS.find((s) => normalized <= s) ?? 10;
  return step * magnitude;
}

const LABEL_COL = { xs: 92, sm: 150 };
const VALUE_COL = { xs: 60, sm: 76 };

// Hand-built horizontal bar list (see git history for why: recharts'
// category-axis text measurement couldn't reliably fit long, variable-length
// service-type names). Bars are scaled against a rounded absolute-dollar
// ceiling with visible tick marks — not against "whatever the largest value
// in this data happens to be" — so a bar's length always means the same
// dollar amount and the top bar isn't visually maxed-out by construction.
// If a category's spend grows past the current ceiling, the scale itself
// grows (new tick marks) and every bar redraws against it; other bars won't
// silently shrink without a visible reason.
function SpendByTypeChart({ data }) {
  const theme = useTheme();
  const rawMax = Math.max(0, ...data.map((item) => item.total));
  const scaleMax = niceCeiling(rawMax);
  const ticks = [0, scaleMax / 4, scaleMax / 2, (3 * scaleMax) / 4, scaleMax];
  const gridColor = alpha(theme.palette.text.primary, 0.08);

  return (
    <Box>
      {/* Tick header */}
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.5, mb: 1 }}>
        <Box sx={{ width: LABEL_COL, flexShrink: 0 }} />
        <Box sx={{ flex: 1, position: "relative", height: 16 }}>
          {ticks.map((tick, i) => {
            const isEdge = i === 0 || i === ticks.length - 1;
            return (
              <Typography
                key={tick}
                variant="caption"
                sx={{
                  position: "absolute",
                  left: `${(tick / scaleMax) * 100}%`,
                  transform:
                    i === 0 ? "translateX(0)" : i === ticks.length - 1 ? "translateX(-100%)" : "translateX(-50%)",
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                  // Middle ticks only fit once the label/value columns have
                  // room to breathe -- on a narrow phone width all 5 labels
                  // collide, so keep just the scale's two ends there.
                  display: isEdge ? "block" : { xs: "none", sm: "block" },
                  ...monoFontSx,
                }}
              >
                ${Math.round(tick).toLocaleString()}
              </Typography>
            );
          })}
        </Box>
        <Box sx={{ width: VALUE_COL, flexShrink: 0 }} />
      </Box>

      {data.map((item) => {
        const pct = Math.max(0.5, (item.total / scaleMax) * 100);
        return (
          <Box
            key={item.service_type}
            title={`${item.service_type}: $${item.total.toFixed(2)}`}
            sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.25 }}
          >
            <Typography
              variant="body2"
              sx={{
                width: LABEL_COL,
                flexShrink: 0,
                color: "text.secondary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.service_type}
            </Typography>
            <Box
              sx={{
                flex: 1,
                height: 20,
                borderRadius: 0.75,
                backgroundColor: alpha(theme.palette.text.primary, 0.06),
                position: "relative",
                overflow: "hidden",
              }}
            >
              {ticks.slice(1).map((tick) => (
                <Box
                  key={tick}
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: `${(tick / scaleMax) * 100}%`,
                    width: "1px",
                    backgroundColor: gridColor,
                  }}
                />
              ))}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: `${pct}%`,
                  backgroundColor: "primary.main",
                  borderRadius: "0 4px 4px 0",
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                width: VALUE_COL,
                flexShrink: 0,
                textAlign: "right",
                fontWeight: 700,
                color: "text.primary",
                ...monoFontSx,
              }}
            >
              ${item.total.toFixed(2)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default SpendByTypeChart;
